import {
  ApplicationConfig,
  provideZonelessChangeDetection,
  importProvidersFrom,
  inject,
} from '@angular/core';
import {
  provideRouter,
  withNavigationErrorHandler,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient } from '@angular/common/http';

import { routes } from './app.routes';
import { AppVersionService } from './core/services/app-version.service';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  getFirestore,
  initializeFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { provideNgxMask } from 'ngx-mask';

// Keep Luxon but optimize it
import {
  MatLuxonDateModule,
  MAT_LUXON_DATE_FORMATS,
} from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';

import { environment } from '../environments/environment';
import { provideIcons } from './core/providers/icons.provider';

export const CUSTOM_DATE_FORMATS = {
  parse: {
    ...MAT_LUXON_DATE_FORMATS.parse,
    dateInput: [
      'D',
      'L/d/y',
      'L/d/yy',
      'L/dd/yy',
      'L/dd/y',
      'L/dd/yyyy',
      'L/d/yyyy',
      'L/d',
      'L/dd',
      'LL/d/y',
      'LL/d/yy',
      'LL/dd/yy',
      'LL/dd/y',
      'LL/dd/yyyy',
      'LL/d/yyyy',
      'LL/d',
      'LL/dd',
    ],
  },
  display: {
    ...MAT_LUXON_DATE_FORMATS.display,
    dateInput: 'DDD',
    dateA11yLabel: 'DDD',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions({ skipInitialTransition: true }),
      withNavigationErrorHandler((navError) => {
        inject(AppVersionService).handleNavigationError(
          navError.error,
          navError.url
        );
      })
    ),
    provideHttpClient(),

    // Single Firebase configuration
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => {
      try {
        return initializeFirestore(getApp(), {
          ignoreUndefinedProperties: true,
        });
      } catch {
        return getFirestore();
      }
    }),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions()),

    // Other providers
    provideNgxMask(),
    provideIcons(),

    // Material date configuration
    importProvidersFrom(MatLuxonDateModule),
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
};
